import { first, get, defer } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import {
  createTopZPosition,
} from '@discoveryedu/titan-grid-layout';
import {
  EDITOR_CANVAS_WIDTH,
  EDITOR_CANVAS_HEIGHT,
  MODAL_CANVAS_WIDTH,
  MODAL_CANVAS_HEIGHT,
  MODAL_CANVAS_PLACE_MODULE_BLEED,
} from '@/lib/constants';
import {
  assetModuleDefault,
  uploadModuleDefault,
} from '@/lib/constants/moduleDefaults';
import { bus } from '@/lib/eventBus';
import {
  bottomYPosition,
} from '@/lib/utils/grid';
import placeModuleWithoutGrid from '@/lib/utils/placeModule';
import canvasUtils from '@/mixins/canvasUtils';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';
import { assetIsUpload } from '@/lib/utils';

export default {
  mixins: [canvasUtils],
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'draftType',
      'getModulesByModalIdOrderedByZPosition',
      'modulesOrderedByZPosition',
      'activeCanvasName',
      'currentModalCanvasId',
      'modalCanvases',
    ]),
    modalCanvas() {
      return this.modalCanvases.find((modal) => modal.id === this.currentModalCanvasId);
    },
    modalCanvasWidth() {
      return this.modalCanvas?.options?.width || MODAL_CANVAS_WIDTH;
    },
    modalCanvasHeight() {
      return this.modalCanvas?.options?.height || MODAL_CANVAS_HEIGHT;
    },
    destinationCanvasName() {
      // The AddModule component has a specified canvas name
      if (this.canvasName) return this.canvasName;

      // Otherwise read from Pinia to decide the current canvas
      return this.currentModalCanvasId
        ? 'modalModules'
        : (this.activeCanvasName || 'modules');
    },
    gridIsOff() {
      return this.destinationCanvasName !== 'notes';
    },
    modulesOrderedByZ() {
      if (this.destinationCanvasName === 'modalModules') {
        return this.getModulesByModalIdOrderedByZPosition(this.currentModalCanvasId);
      }

      return this.modulesOrderedByZPosition;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODULE,
      types.REPLACE_MODULE,
    ]),
    selectModulesOnCanvas(moduleIds) {
      defer(() => {
        bus.emit('grid-layout:set-selection', {
          canvasName: this.destinationCanvasName,
          ids: moduleIds,
        });

        if (this.destinationCanvasName === 'notes') {
          defer(() => {
            // NotesCanvas "auto-scroll" is disabled. If it needs to auto-scroll
            // it needs to be done by emitting this event-bus id.
            bus.emit('notes-canvas:scroll-selection-into-view');
          });
        }

        if (moduleIds?.length === 1) {
          bus.emit('grid-item:focus', {
            canvasName: this.destinationCanvasName,
            gridItemId: first(moduleIds),
          });
        }
      });
    },
    async addModulesFromMyContent(assets) {
      if (!assets.length) return;

      const addPromises = assets.map((asset) => {
        // Users can create other assets like TEAs or Boards,
        // but those are not really "uploads"
        const isUpload = assetIsUpload(asset);

        const mod = isUpload
          ? uploadModuleDefault(this.gridIsOff)
          : assetModuleDefault(this.gridIsOff, asset);
        mod.options.asset_id = asset.id;

        return this.placeModule(mod, false);
      });

      const moduleIds = await Promise.all(addPromises);

      this.selectModulesOnCanvas(moduleIds);
    },
    async addModuleToState(mod, layoutOverrides, selectOnCanvas = true) {
      let newModuleId;
      const moduleToAdd = {
        ...mod,
        layout: {
          ...mod.layout,
          ...layoutOverrides,
        },
      };

      if (this.moduleToReplace) {
        newModuleId = await this[types.REPLACE_MODULE]({
          canvasName: this.destinationCanvasName,
          payload: moduleToAdd,
          replacedModuleId: this.moduleToReplace.id,
        });
      } else {
        newModuleId = await this[types.ADD_MODULE]({
          canvasName: this.destinationCanvasName,
          payload: moduleToAdd,
        });
      }

      if (selectOnCanvas) {
        this.selectModulesOnCanvas([newModuleId]);
      }

      return newModuleId;
    },
    async placeModule(mod, selectOnCanvas = true) {
      let newModuleId;
      if (this.moduleToReplace) {
        // If we have a module we're replacing, we already
        // know the destination coordinates
        newModuleId = await this.addModuleToState(
          {
            ...mod,
            options: {
              ...this.moduleToReplace.options,
              ...mod.options,
            },
          },
          this.moduleToReplace.layout,
          selectOnCanvas,
        );
      } else if (this.destinationCanvasName !== 'notes') {
        let canvasInfo;

        if (this.destinationCanvasName === 'modalModules') {
          canvasInfo = {
            width: this.modalCanvasWidth,
            height: this.modalCanvasHeight,
            bleed: MODAL_CANVAS_PLACE_MODULE_BLEED,
          };
        } else {
          canvasInfo = {
            width: EDITOR_CANVAS_WIDTH,
            height: this.currentPage?.options?.height || EDITOR_CANVAS_HEIGHT,
          };
        }

        canvasInfo.defaultPosition = this.getVisibleCanvasCenter(mod);

        const coordinates = placeModuleWithoutGrid(
          this.modulesOrderedByZ,
          mod,
          canvasInfo,
        );

        const layoutOverrides = {
          x_position: coordinates.x,
          y_position: coordinates.y,
        };

        // Assign the next z coordinate above other modules.
        layoutOverrides.z_position = createTopZPosition({
          itemsOrderedByZ: this.modulesOrderedByZ,
          zProperty: 'layout.z_position',
        });

        newModuleId = await this.addModuleToState(
          mod,
          layoutOverrides,
          selectOnCanvas,
        );
      } else {
        newModuleId = await this.addModuleToState(
          mod,
          {
            x_position: mod.layout.x_position,
            y_position: bottomYPosition(get(this, 'currentPage.notes', [])),
          },
          selectOnCanvas,
        );
      }

      // Close the menu now that the module has been added to the canvas
      this.menuVisible = false;

      return newModuleId;
    },
  },
};
